import React, { useState } from "react";
import { isMobile } from "react-device-detect";
import CreatableSelect from "react-select/creatable";

import { CustomDropdownWithChevron } from "../../../components";
import { useModalContext } from "../../../contexts/ModalContext";
import { useAppDispatch, useAppSelector } from "../../../redux/hooks";
import { uploadSessionFormSlice } from "../../../redux/slices/uploadSessionFormSlice";
import {
  customSelectStyles,
  emptyPlayerFormValues,
} from "../../../shared/Constants";
import { disableAddPlayer, toTitleCase } from "../../../shared/Functions";
import { Player } from "../../../types";
import { PlayerForm } from "../../Player/PlayerForm";

type SelectPlayerStepProps = {
  players: Player[];
};

export const SelectPlayerStep: React.FC<SelectPlayerStepProps> = ({
  players,
}) => {
  // State and AppContext
  const [, setIsMenuOpen] = useState(false);
  const { setModalState } = useModalContext();

  // RTK slice actions
  const dispatch = useAppDispatch();
  const { storeUploadSessionFormState } = uploadSessionFormSlice.actions;

  // Redux calls
  const uploadSessionFormValues = useAppSelector(
    (state) => state.uploadSessionForm,
  );
  const orgType = useAppSelector((state) => state.org.type);

  const onMenuOpen = () => setIsMenuOpen(true);
  const onMenuClose = () => setIsMenuOpen(false);

  const playerOptions = players.map((player) => {
    const name = toTitleCase(`${player.firstName} ${player.lastName}`);
    return { value: `${player.id}`, label: name };
  });
  const stepTitleNode = document.querySelector("#step-1-title");

  function handlePlayerChange(
    player: { value: string; label: string; __isNew__?: boolean } | null,
  ): void {
    if (player && !player["__isNew__"]) {
      if (stepTitleNode) {
        stepTitleNode.innerHTML = `
          <div class="hidden sm:flex">Select Player</div>
          <div class="text-gray-500 font-bold text-[12px] sm:text-sm">${
            player.label.split(" ")[0]
          } ${player.label.split(" ")[1]}</div>`;
      }
      dispatch(
        storeUploadSessionFormState({
          ...uploadSessionFormValues,
          selectedPlayer: player,
        }),
      );
    } else if (player && player["__isNew__"] && isMobile) {
      return;
    } else if (!disableAddPlayer(orgType)) {
      setModalState({
        shouldOpen: true,
        title: "Add New Player",
        singleStep: <PlayerForm formValues={emptyPlayerFormValues} />,
        steps: [],
        currentStep: 1,
      });
    }
  }

  return (
    <div
      className="flex sm:justify-center flex-col w-full sm:w-2/6 mx-auto sm:h-96 px-4 mt-4 sm:p-0 sm:mt-0"
      data-testid="select-player-container"
    >
      <div className="h-16 mb-6">
        <div className="flex">
          <label
            id="player-select-label"
            htmlFor="player-select"
            className="block text-sm font-medium text-gray-500 pb-1"
          >
            Player
          </label>
          <span
            className="text-red-500 text-sm"
            data-testid="player-required-asterisk"
          >
            *
          </span>
        </div>
        <CreatableSelect
          aria-labelledby="player-select-label"
          inputId="player-select-input"
          classNamePrefix="player-select"
          onMenuOpen={onMenuOpen}
          onMenuClose={onMenuClose}
          options={playerOptions}
          value={
            uploadSessionFormValues.selectedPlayer.value === ""
              ? null
              : uploadSessionFormValues.selectedPlayer
          }
          placeholder="Select Player"
          onChange={(player) => {
            handlePlayerChange(player);
          }}
          formatCreateLabel={(): string => {
            if (disableAddPlayer(orgType) || isMobile) {
              return "Player Not Found";
            } else {
              return "+ Create New Player";
            }
          }}
          styles={customSelectStyles}
          components={{
            DropdownIndicator: CustomDropdownWithChevron,
            IndicatorSeparator: () => null,
          }}
          maxMenuHeight={isMobile ? 200 : 225}
        />
      </div>
    </div>
  );
};
